import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, TextField, Select, MenuItem, FormControl, InputLabel, Chip, IconButton,
  Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector, useDispatch } from 'react-redux';
import { login } from '../store/authSlice';
import moderationService from '../services/moderationService';

const PendingModeration = () => {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [moderations, setModerations] = useState([]);
  const [filteredModerations, setFilteredModerations] = useState([]);
  const [processing, setProcessing] = useState({});

  const [query, setQuery] = useState('');
  const [filterBy, setFilterBy] = useState('fileName');
  const [statusFilter, setStatusFilter] = useState('all');

  const [showMediaModal, setShowMediaModal] = useState(false);
  const [modalVideoUrl, setModalVideoUrl] = useState('');

  const [showNotesModal, setShowNotesModal] = useState(false);
  const [currentModerationId, setCurrentModerationId] = useState(null);
  const [currentAction, setCurrentAction] = useState(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    dispatch(login(user));
    fetchModerations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (moderations.length > 0) performSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBy, statusFilter, query]);

  const fetchModerations = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await moderationService.getPendingModeration(100, 0);
      setModerations(Array.isArray(data) ? data : []);
      setFilteredModerations(Array.isArray(data) ? data : []);
      setQuery('');
      setFilterBy('fileName');
      setStatusFilter('all');
    } catch (err) {
      setError('Failed to fetch moderations: ' + (err?.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const openNotesModal = (moderationId, action) => {
    setCurrentModerationId(moderationId);
    setCurrentAction(action);
    setNotes('');
    setShowNotesModal(true);
  };

  const closeNotesModal = () => {
    setShowNotesModal(false);
    setCurrentModerationId(null);
    setCurrentAction(null);
    setNotes('');
  };

  const handleConfirmAction = async () => {
    if (!currentModerationId || !currentAction) return;
    setProcessing({ ...processing, [currentModerationId]: true });
    try {
      if (currentAction === 'approve') {
        await moderationService.approveVideo(currentModerationId, notes);
      } else {
        await moderationService.rejectVideo(currentModerationId, notes);
      }
      closeNotesModal();
      await fetchModerations();
    } catch (err) {
      alert(`Failed to ${currentAction} video: ` + (err?.message || 'Unknown error'));
    } finally {
      setProcessing({ ...processing, [currentModerationId]: false });
    }
  };

  const performSearch = () => {
    let filtered = moderations;
    if (statusFilter !== 'all') filtered = filtered.filter(m => m.status === statusFilter);
    const trimmed = (query || '').trim();
    if (trimmed) {
      const q = trimmed.toLowerCase();
      filtered = filtered.filter((m) => {
        if (filterBy === 'fileName') return (m?.fileName || '').toLowerCase().includes(q);
        if (filterBy === 'status') return (m?.status || '').toLowerCase().includes(q);
        return (m?.fileName || '').toLowerCase().includes(q);
      });
    }
    setFilteredModerations(filtered);
  };

  const handleRemoveFilter = () => { setQuery(''); setStatusFilter('all'); setFilteredModerations(moderations); };
  const isFilteredActive = () => (query || '').trim() || statusFilter !== 'all' || filteredModerations.length !== moderations.length;
  const copyToClipboard = (text) => navigator.clipboard.writeText(text);

  const getStatusChip = (status, score, flags) => {
    if (status === 'rejected') return <Chip label="REJECTED" size="small" sx={{ bgcolor: 'rgba(244,67,54,0.15)', color: '#e57373', fontWeight: 600 }} />;
    if (status === 'approved') return <Chip label="APPROVED" size="small" sx={{ bgcolor: 'rgba(76,175,80,0.15)', color: '#81c784', fontWeight: 600 }} />;
    if (score >= 0.7) return <Chip label={`HIGH RISK (${score.toFixed(2)})`} size="small" sx={{ bgcolor: 'rgba(244,67,54,0.15)', color: '#e57373', fontWeight: 600 }} />;
    if (score >= 0.5) return <Chip label={`REVIEW (${score.toFixed(2)})`} size="small" sx={{ bgcolor: 'rgba(255,152,0,0.15)', color: '#ffb74d', fontWeight: 600 }} />;
    if (flags && flags.includes('workout_context_detected')) return <Chip label={`WORKOUT (${score.toFixed(2)})`} size="small" sx={{ bgcolor: 'rgba(100,181,246,0.15)', color: '#64b5f6', fontWeight: 600 }} />;
    return <Chip label={`PENDING (${score?.toFixed?.(2) || '—'})`} size="small" sx={{ bgcolor: 'rgba(139,148,158,0.15)', color: '#8b949e', fontWeight: 600 }} />;
  };

  const exportExcel = () => {
    if (!filteredModerations || filteredModerations.length === 0) return;
    const rows = filteredModerations.map((m, index) => ({
      No: index + 1, FileName: m.fileName || '-', Status: m.status || '-',
      Score: typeof m.moderationScore === 'number' ? m.moderationScore.toFixed(2) : '-',
      Flags: (m.flags || []).join(', ') || '-', VideoUrl: m.videoUrl || '-',
    }));
    const csvContent = 'data:text/csv;charset=utf-8,' +
      [Object.keys(rows[0]).join(','), ...rows.map((r) => Object.values(r).map((v) => `"${v}"`).join(','))].join('\n');
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csvContent));
    link.setAttribute('download', `moderation_${new Date().toISOString()}.csv`);
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}><CircularProgress sx={{ color: '#2bb6a9' }} /></Box>;
  }

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Field</InputLabel>
          <Select value={filterBy} label="Field" onChange={(e) => setFilterBy(e.target.value)}>
            <MenuItem value="fileName">File Name</MenuItem>
            <MenuItem value="status">Status</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
          </Select>
        </FormControl>
        <TextField size="small" placeholder="Search..." value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && performSearch()} sx={{ minWidth: 180 }} />
        <Button size="small" variant="contained" startIcon={<SearchIcon />} onClick={performSearch}>Search</Button>
        <Button size="small" variant="outlined" color="error" startIcon={<ClearIcon />} onClick={handleRemoveFilter} disabled={!isFilteredActive()}>Clear</Button>
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportExcel}>Export</Button>
        <IconButton size="small" onClick={fetchModerations} sx={{ color: '#8b949e' }}><RefreshIcon /></IconButton>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={0} sx={{ border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 260px)' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>File Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Score</TableCell>
                <TableCell>Flags</TableCell>
                <TableCell>Video</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredModerations.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#8b949e' }}>No pending moderations found.</TableCell></TableRow>
              ) : filteredModerations.map((m, index) => {
                const fileName = m.fileName || '-';
                const shortFileName = fileName.length > 30 ? fileName.slice(0, 30) + '...' : fileName;
                return (
                  <TableRow key={m._id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{shortFileName}</Typography>
                        <IconButton size="small" onClick={() => copyToClipboard(fileName)} sx={{ color: '#8b949e', p: 0.3 }}><ContentCopyIcon sx={{ fontSize: 14 }} /></IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>{getStatusChip(m.status, m.moderationScore, m.flags)}</TableCell>
                    <TableCell sx={{ fontSize: '0.85rem' }}>{typeof m.moderationScore === 'number' ? m.moderationScore.toFixed(2) : '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
                        {(m.flags || []).map((flag, idx) => (
                          <Chip key={idx} label={flag} size="small" sx={{ bgcolor: 'rgba(139,148,158,0.15)', color: '#8b949e', fontSize: '0.7rem' }} />
                        ))}
                        {(m.audioFlags || []).map((flag, idx) => (
                          <Chip key={`audio-${idx}`} label={`🎵 ${flag}`} size="small" sx={{ bgcolor: 'rgba(255,152,0,0.15)', color: '#ffb74d', fontSize: '0.7rem' }} />
                        ))}
                        {m.audioFingerprint && m.copyrightAction === 'REJECTED' && (
                          <Chip label={`🎵 ${m.audioFingerprint.artist} - ${m.audioFingerprint.musicTitle} (AUTO-REJECTED)`} size="small" sx={{ bgcolor: 'rgba(244,67,54,0.15)', color: '#e57373', fontSize: '0.7rem' }} />
                        )}
                        {m.audioFingerprint && m.copyrightAction === 'PENDING_REVIEW' && (
                          <Chip label={`🎵 ${m.audioFingerprint.artist} - ${m.audioFingerprint.musicTitle} (REVIEW)`} size="small" sx={{ bgcolor: 'rgba(255,152,0,0.15)', color: '#ffb74d', fontSize: '0.7rem' }} />
                        )}
                        {(!m.flags || m.flags.length === 0) && (!m.audioFlags || m.audioFlags.length === 0) && !m.audioFingerprint && (
                          <Typography variant="body2" sx={{ color: '#8b949e' }}>—</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {m.videoUrl ? (
                        <IconButton size="small" onClick={() => { setModalVideoUrl(m.videoUrl); setShowMediaModal(true); }} sx={{ color: '#ff9800' }}><PlayCircleOutlineIcon fontSize="small" /></IconButton>
                      ) : <Typography variant="body2" sx={{ color: '#8b949e' }}>—</Typography>}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Button size="small" variant="contained" color="success" onClick={() => openNotesModal(m._id, 'approve')} disabled={processing[m._id] || m.status === 'approved'} sx={{ minWidth: 0, px: 1.5, fontSize: '0.7rem' }}>Approve</Button>
                        <Button size="small" variant="contained" color="error" onClick={() => openNotesModal(m._id, 'reject')} disabled={processing[m._id] || m.status === 'rejected'} sx={{ minWidth: 0, px: 1.5, fontSize: '0.7rem' }}>Reject</Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Video Dialog */}
      <Dialog open={showMediaModal} onClose={() => { setShowMediaModal(false); setModalVideoUrl(''); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Video Preview
          <IconButton onClick={() => { setShowMediaModal(false); setModalVideoUrl(''); }} sx={{ color: '#8b949e' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          {modalVideoUrl && <video controls style={{ width: '100%', maxHeight: '70vh', borderRadius: 8 }}><source src={modalVideoUrl} /></video>}
        </DialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={showNotesModal} onClose={closeNotesModal} maxWidth="sm" fullWidth>
        <DialogTitle>{currentAction === 'approve' ? 'Approve Video' : 'Reject Video'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Add notes about this decision..." sx={{ mt: 1 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeNotesModal} sx={{ color: '#8b949e' }}>Cancel</Button>
          <Button variant="contained" color={currentAction === 'approve' ? 'success' : 'error'} onClick={handleConfirmAction} disabled={processing[currentModerationId]}>
            {processing[currentModerationId] ? 'Processing...' : `Confirm ${currentAction === 'approve' ? 'Approval' : 'Rejection'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PendingModeration;
